import { amountInWords as sharedAmountInWords } from "../../../utils/helpers/numberToWords";

/**
 * Algerian HR document definitions and French rendering helpers.
 *
 * The printed body is always French: an Algerian bank, consulate or
 * administration will not accept an English attestation, so these strings are
 * intentionally not routed through i18n. Only the surrounding UI is translated.
 */

export const DOCUMENT_TYPES = [
  {
    value: 'attestation_travail',
    label: 'Attestation de travail',
    hint: 'Atteste qu\'un salarié en poste travaille dans l\'entreprise',
    fields: ['birthplace'],
  },
  {
    value: 'certificat_travail',
    label: 'Certificat de travail',
    hint: 'Remis en fin de contrat — obligatoire (art. 67, loi 90-11)',
    fields: ['birthplace', 'end_date', 'positions_held'],
  },
  {
    value: 'attestation_salaire',
    label: 'Attestation de salaire',
    hint: 'Détaille la rémunération, pour une banque ou un visa',
    fields: ['birthplace', 'purpose'],
  },
  {
    value: 'ordre_mission',
    label: 'Ordre de mission',
    hint: 'Autorise un déplacement professionnel',
    fields: ['destination', 'purpose', 'transport', 'departure_date', 'return_date', 'expenses_covered'],
  },
  {
    value: 'decision_affectation',
    label: 'Décision d\'affectation',
    hint: 'Acte de nomination ou de changement de poste',
    fields: ['new_position', 'new_department', 'effective_date', 'reason'],
  },
  {
    value: 'autorisation_heures_supplementaires',
    label: 'Autorisation d\'heures supplémentaires',
    hint: 'Autorise des heures au-delà de la durée légale',
    fields: ['hours', 'from_date', 'to_date', 'reason'],
  },
];

export const documentTypeLabel = (type) =>
  DOCUMENT_TYPES.find(d => d.value === type)?.label ?? type;

/** French civility from the stored gender, defaulting to the neutral form. */
export const civility = (gender) => {
  if (gender === 'male') return 'Monsieur';
  if (gender === 'female') return 'Madame';
  return 'Monsieur/Madame';
};

export const shortCivility = (gender) => {
  if (gender === 'male') return 'M.';
  if (gender === 'female') return 'Mme';
  return 'M./Mme';
};

/** "né" / "née" agreement. */
export const born = (gender) => (gender === 'female' ? 'née' : 'né(e)');
export const employed = (gender) => (gender === 'female' ? 'employée' : 'employé(e)');
export const concerned = (gender) => (gender === 'female' ? "l'intéressée" : "l'intéressé(e)");

/** Algerian administrative date format. */
export const frDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

export const frAmount = (value) =>
  (Number(value) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * HR documents are always issued in French, so the amount is spelled in French
 * regardless of the interface language. The implementation lives in the shared
 * helper alongside the Arabic and English spellings used by invoices.
 */
export const amountInWords = (value) => sharedAmountInWords(value, 'fr', 'dzd');

/** Whole days between two dates, inclusive, the way a mission order counts them. */
export const daysBetween = (from, to) => {
  if (!from || !to) return 0;
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(0, Math.round((end - start) / 86400000) + 1);
};
